/**
 * Leitores puros da identidade do imóvel exibido na Área QPreço.
 *
 * Extraídos de `pages/AreaQprecoIndex.vue` preservando as mesmas cadeias de
 * fallback, os mesmos rótulos e os mesmos tokens de cor.
 */
import {
  clampPercentage,
  formatNumber,
  numericValue,
  presentValue,
} from './qprecoFormatters';

export const isExternalItem = item =>
  Boolean(item?.external) || String(item?.id || '').startsWith('external:');

export const primaryItemPrice = item =>
  ['aluguel', 'temporada'].includes(item?.finalidade)
    ? item?.valor_aluguel
    : item?.valor_venda;

export const propertyIdentity = item => item?.property_identity || item || {};

export const propertyCode = item =>
  propertyIdentity(item)?.code ||
  item?.codigo ||
  item?.external_id ||
  item?.imovel_id ||
  item?.interest_id ||
  item?.id ||
  'Sem código';

export const propertyTitle = item =>
  propertyIdentity(item)?.title ||
  item?.imovel_titulo ||
  item?.titulo ||
  item?.address ||
  'Imóvel';

export const propertyAddress = item =>
  propertyIdentity(item)?.addressDetails ||
  item?.addressDetails ||
  item?.imovel_endereco ||
  item?.address ||
  item?.endereco ||
  [item?.bairro, item?.cidade, item?.uf].filter(Boolean).join(', ') ||
  'Endereço não informado';

export const propertyDetails = item =>
  propertyIdentity(item)?.propertyDetails ||
  item?.propertyDetails ||
  [
    item?.tipo_label || item?.tipo,
    numericValue(item?.area_util || item?.area)
      ? `${formatNumber(item?.area_util || item?.area)} m²`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

export const propertyBedrooms = item =>
  propertyIdentity(item)?.bedrooms || item?.quartos || item?.bedrooms || 0;

export const propertyParkingSpaces = item =>
  propertyIdentity(item)?.parking_spaces ||
  propertyIdentity(item)?.parkingSpaces ||
  item?.vagas_garagem ||
  item?.vagas ||
  item?.parkingSpaces ||
  0;

export const propertyValue = item =>
  propertyIdentity(item)?.current_price ||
  propertyIdentity(item)?.currentPrice ||
  primaryItemPrice(item) ||
  item?.estimate_value;

export const propertyArea = item =>
  propertyIdentity(item)?.area ||
  propertyIdentity(item)?.area_util ||
  item?.area_util ||
  item?.area ||
  0;

export const propertyBathrooms = item =>
  propertyIdentity(item)?.bathrooms || item?.banheiros || item?.bathrooms || 0;

export const propertyHistoryCount = item =>
  numericValue(
    presentValue(
      item?.price_history_count,
      item?.priceHistoryCount,
      item?.price_history_meta?.count,
      item?.priceHistoryMeta?.count
    )
  );

export const propertySourceLabel = item =>
  isExternalItem(item) ? 'Base integrada' : 'Carteira interna';

export const propertySourceIcon = item =>
  isExternalItem(item) ? 'i-lucide-database' : 'i-lucide-home';

export const propertySourceTone = item =>
  isExternalItem(item)
    ? 'border-lais-primary-50 bg-lais-primary-50 text-lais-primary'
    : 'border-emerald-100 bg-emerald-50 text-emerald-800';

export const confidenceTone = item =>
  item?.confidence === 'high'
    ? 'border-emerald-100 bg-emerald-50 text-emerald-800'
    : 'border-amber-100 bg-amber-50 text-amber-800';

export const confidenceLabel = item =>
  item?.confidence === 'high' ? 'Alta confiança' : 'Dados limitados';

export const propertyStats = item => [
  {
    key: 'area',
    icon: 'i-lucide-ruler',
    label: 'Área',
    value: propertyArea(item) ? `${formatNumber(propertyArea(item))} m²` : '—',
  },
  {
    key: 'bedrooms',
    icon: 'i-lucide-bed',
    label: 'Quartos',
    value: formatNumber(propertyBedrooms(item)),
  },
  {
    key: 'parking',
    icon: 'i-lucide-car',
    label: 'Vagas',
    value: formatNumber(propertyParkingSpaces(item)),
  },
  {
    key: 'bathrooms',
    icon: 'i-lucide-bath',
    label: 'Banheiros',
    value: formatNumber(propertyBathrooms(item)),
  },
];

export const propertyCompletion = item => {
  const identity = propertyIdentity(item);
  const explicit = presentValue(
    identity?.completenessPercentage,
    identity?.completeness_percentage,
    item?.completenessPercentage,
    item?.completeness_percentage
  );
  if (explicit !== undefined) return clampPercentage(explicit);

  const requiredFields = [
    propertyAddress(item),
    identity?.cep || item?.cep,
    identity?.tipo || item?.tipo,
    propertyBedrooms(item),
    propertyArea(item),
    propertyValue(item),
  ];
  const filled = requiredFields.filter(value => Boolean(value)).length;
  return clampPercentage((filled / requiredFields.length) * 100);
};

export const propertySaleEstimate = item =>
  presentValue(
    propertyIdentity(item)?.sale?.priceEstimation,
    item?.sale?.priceEstimation,
    propertyIdentity(item)?.sale_price,
    item?.valor_venda
  );

export const propertyRentEstimate = item =>
  presentValue(
    propertyIdentity(item)?.rent?.priceEstimation,
    item?.rent?.priceEstimation,
    propertyIdentity(item)?.rent_price,
    item?.valor_aluguel
  );

export const propertySourceUrl = item =>
  presentValue(
    propertyIdentity(item)?.link_externo,
    propertyIdentity(item)?.source_url,
    item?.link_externo,
    item?.source_url
  );

export const completionScore = imovel => {
  return propertyCompletion(imovel);
};

export const needsComplement = item =>
  !isExternalItem(item) && completionScore(item) < 80;
